import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, Switch, message, Typography, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE } from '../config/api';

const { Title, Text } = Typography;
const { Search } = Input;

const VendorManagementPage = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form] = Form.useForm();

  // 업체 목록 조회
  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/bms/vendors`);
      if (response.data.success) {
        setVendors(response.data.data);
        setFilteredVendors(response.data.data);
      } else {
        message.error('업체 목록을 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('업체 조회 오류:', error);
      
      // 임시: API가 없을 때 목업 데이터 사용
      if (error.response?.status === 404) {
        message.warning('API 서버에 /api/bms/vendors 엔드포인트를 구현해주세요. 임시 데이터를 표시합니다.');
        const mockData = [
          {
            vendor_id: 1,
            vendor_code: 'V001',
            vendor_name: '테스트 업체',
            contact_name: '홍길동',
            contact_phone: '010-1234-5678',
            contact_email: 'test@example.com',
            is_active: 1
          }
        ];
        setVendors(mockData);
        setFilteredVendors(mockData);
      } else {
        message.error('서버 연결에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // 검색 기능
  const handleSearch = (value) => {
    const searchText = value.toLowerCase();
    const filtered = vendors.filter(v => 
      v.vendor_name.toLowerCase().includes(searchText) || 
      v.vendor_code.toLowerCase().includes(searchText) ||
      (v.contact_name && v.contact_name.toLowerCase().includes(searchText))
    );
    setFilteredVendors(filtered);
  };

  // 모달 열기 (새로 추가 또는 수정)
  const showModal = (vendor = null) => {
    setEditingVendor(vendor);
    if (vendor) {
      form.setFieldsValue({
        vendor_code: vendor.vendor_code,
        vendor_name: vendor.vendor_name,
        contact_name: vendor.contact_name,
        contact_phone: vendor.contact_phone,
        contact_email: vendor.contact_email,
        is_active: vendor.is_active === 1,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
    }
    setModalVisible(true);
  };

  // 모달 닫기
  const handleCancel = () => {
    setModalVisible(false);
    setEditingVendor(null);
    form.resetFields();
  };

  // 업체 저장 (추가 또는 수정)
  const handleSave = async (values) => {
    const vendorData = {
      ...values,
      is_active: values.is_active ? 1 : 0,
    };

    // 새로 추가할 때 업체 코드가 없으면 자동 생성
    if (!editingVendor && !vendorData.vendor_code) {
      const timestamp = Date.now().toString().slice(-6);
      vendorData.vendor_code = `V${timestamp}`;
    }

    try {
      if (editingVendor) {
        // 수정
        const response = await axios.put(`${API_BASE}/api/bms/vendors/${editingVendor.vendor_id}`, vendorData);
        if (response.data.success) {
          message.success('업체 정보가 수정되었습니다.');
          fetchVendors();
          handleCancel();
        } else {
          message.error(response.data.message || '업체 수정에 실패했습니다.');
        }
      } else {
        // 추가
        const response = await axios.post(`${API_BASE}/api/bms/vendors`, vendorData);
        if (response.data.success) {
          message.success('새 업체가 추가되었습니다.');
          fetchVendors();
          handleCancel();
        } else {
          message.error(response.data.message || '업체 추가에 실패했습니다.');
        }
      }
    } catch (error) {
      console.error('업체 저장 오류:', error);
      message.error('서버 오류가 발생했습니다.');
    }
  };

  // 업체 삭제
  const handleDelete = (vendor) => {
    Modal.confirm({
      title: '업체 삭제',
      content: `"${vendor.vendor_name}" 업체를 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          const response = await axios.delete(`${API_BASE}/api/bms/vendors/${vendor.vendor_id}`);
          if (response.data.success) {
            message.success('업체가 삭제되었습니다.');
            fetchVendors();
          } else {
            message.error(response.data.message || '업체 삭제에 실패했습니다.');
          }
        } catch (error) {
          console.error('업체 삭제 오류:', error);
          message.error('서버 오류가 발생했습니다.');
        }
      },
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'vendor_id',
      key: 'vendor_id',
      width: 80,
      align: 'center',
    },
    {
      title: '업체 코드',
      dataIndex: 'vendor_code',
      key: 'vendor_code',
      width: 120,
    },
    {
      title: '업체명',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      width: 200,
    },
    {
      title: '담당자',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 120,
    },
    {
      title: '연락처',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 150,
    },
    {
      title: '이메일',
      dataIndex: 'contact_email',
      key: 'contact_email',
      width: 200,
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (is_active) => (
        <Tag color={is_active === 1 ? 'green' : 'red'}>
          {is_active === 1 ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '작업',
      key: 'action',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showModal(record)}
          >
            수정
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            onClick={() => handleDelete(record)}
          >
            삭제
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Card 
        title={
          <Space>
            <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <Title level={3} style={{ margin: 0 }}>거래처 관리</Title>
          </Space>
        }
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            업체 추가
          </Button>
        }
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">
              등록된 거래처를 관리하고, 새로운 업체를 추가할 수 있습니다.
            </Text>
          </div>

          <Search
            placeholder="업체명, 코드, 담당자로 검색"
            allowClear
            enterButton="검색"
            size="large"
            onSearch={handleSearch}
            onChange={(e) => !e.target.value && handleSearch('')}
            style={{ maxWidth: '500px' }}
          />

          <Table
            columns={columns}
            dataSource={filteredVendors}
            rowKey="vendor_id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `총 ${total}개 업체`,
            }}
            bordered
            scroll={{ x: 'max-content' }}
          />
        </Space>
      </Card>

      <Modal
        title={editingVendor ? '업체 정보 수정' : '새 업체 추가'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            label="업체 코드"
            name="vendor_code"
            rules={[
              { pattern: /^[A-Z0-9]+$/, message: '대문자와 숫자만 입력 가능합니다' },
            ]}
            extra="비워두면 자동 생성됩니다"
          >
            <Input placeholder="자동 생성 (직접 입력 가능)" disabled={!!editingVendor} />
          </Form.Item>

          <Form.Item
            label="업체명"
            name="vendor_name"
            rules={[{ required: true, message: '업체명을 입력해주세요' }]}
          >
            <Input placeholder="업체명 입력" />
          </Form.Item>

          <Form.Item
            label="담당자"
            name="contact_name"
          >
            <Input placeholder="담당자 이름 입력" />
          </Form.Item>

          <Form.Item
            label="연락처"
            name="contact_phone"
            rules={[
              { pattern: /^[0-9-]+$/, message: '숫자와 하이픈(-)만 입력 가능합니다' },
            ]}
          >
            <Input placeholder="010-1234-5678" />
          </Form.Item>

          <Form.Item
            label="이메일"
            name="contact_email"
            rules={[
              { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
            ]}
          >
            <Input placeholder="example@email.com" />
          </Form.Item>

          <Form.Item
            label="활성 상태"
            name="is_active"
            valuePropName="checked"
          >
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                취소
              </Button>
              <Button type="primary" htmlType="submit">
                {editingVendor ? '수정' : '추가'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VendorManagementPage;
